/**
 * Top bar: menu (phones), branch switcher, global search, sync status, alerts, theme,
 * user menu. Search accepts an item number, serial, description or a scanned EPC.
 */
import { Bell, Building2, Check, CloudOff, Cloud, KeyRound, LogOut, Menu, Monitor, Moon, RefreshCw, Sun, TriangleAlert, UserRound } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, DateText, IconButton, MenuItem, Popover, SearchInput, cx } from '../../components/ui';
import { useAuth } from '../../lib/auth';
import { ALL, useBranch } from '../../lib/branch';
import { count } from '../../lib/format';
import { useOnline } from '../../lib/online';
import { retryEntry, removeEntry } from '../../lib/offlineQueue';
import { useAlerts } from '../../lib/queries';
import { useQueue } from '../../lib/useQueue';
import { useTheme } from '../../lib/theme';

export function TopBar({ onMenu }: { onMenu: () => void }) {
  return (
    <header className="sticky top-0 z-30 flex h-16 items-center gap-2 border-b border-line bg-card/95 px-2 backdrop-blur sm:px-4">
      <IconButton label="Open menu" onClick={onMenu} className="lg:hidden">
        <Menu aria-hidden className="h-5 w-5" />
      </IconButton>
      <BranchSwitcher />
      <GlobalSearch />
      <div className="ml-auto flex items-center gap-0.5">
        <SyncStatus />
        <AlertsBell />
        <ThemeToggle />
        <UserMenu />
      </div>
    </header>
  );
}

function BranchSwitcher() {
  const { user } = useAuth();
  const { branchId, setBranchId, branch, options, canSeeAll } = useBranch();
  const label = branch ? branch.name : 'All branches';
  if (!canSeeAll && options.length <= 1)
    return (
      <span className="inline-flex min-h-touch min-w-0 items-center gap-2 truncate px-2 text-sm font-semibold">
        <Building2 aria-hidden className="h-4 w-4 text-muted" />
        {options[0]?.name ?? (user?.allBranches ? 'All branches' : 'No branch assigned')}
      </span>
    );
  return (
    <Popover
      label="Branch"
      trigger={({ open, toggle, id }) => (
        <button type="button" aria-haspopup="menu" aria-expanded={open} aria-controls={id} onClick={toggle} className="inline-flex min-h-touch max-w-[46vw] items-center gap-2 rounded-tag border border-line px-3 text-sm font-semibold hover:bg-sunken sm:max-w-[240px]">
          <Building2 aria-hidden className="h-4 w-4 shrink-0 text-muted" />
          <span className="truncate">{label}</span>
          {branch && <span className="code hidden text-xs text-muted sm:inline">{branch.code}</span>}
        </button>
      )}
    >
      {(close) => (
        <div className="max-h-[60vh] w-72 overflow-auto">
          {canSeeAll && (
            <MenuItem
              onClick={() => {
                setBranchId(ALL);
                close();
              }}
              icon={branchId === ALL ? <Check className="text-brand" /> : <span className="block h-4 w-4" />}
            >
              All branches
            </MenuItem>
          )}
          {options.map((b) => (
            <MenuItem
              key={b.id}
              onClick={() => {
                setBranchId(b.id);
                close();
              }}
              icon={branchId === b.id ? <Check className="text-brand" /> : <span className="block h-4 w-4" />}
            >
              <span className="flex justify-between gap-3">
                {b.name}
                <span className="code text-xs text-muted">{b.code}</span>
              </span>
            </MenuItem>
          ))}
        </div>
      )}
    </Popover>
  );
}

function GlobalSearch() {
  const [q, setQ] = useState('');
  const navigate = useNavigate();
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const term = q.trim();
    if (!term) return;
    navigate(`/inventory/items?q=${encodeURIComponent(term)}`);
    setQ('');
  };
  return (
    <form role="search" onSubmit={submit} className="hidden min-w-0 flex-1 md:block md:max-w-md">
      <SearchInput label="Search items by tag, item number, serial or description" placeholder="Search items, tags, serials" value={q} onChange={setQ} />
    </form>
  );
}

function SyncStatus() {
  const online = useOnline();
  const { entries, pending, failed, syncing, syncNow } = useQueue();
  const waiting = pending + failed;
  const tone = !online ? 'text-warn' : failed ? 'text-danger' : pending ? 'text-brass' : 'text-muted';
  const label = !online ? `Offline${waiting ? `, ${waiting} waiting to sync` : ''}` : failed ? `${failed} batch${failed === 1 ? '' : 'es'} need attention` : pending ? `${pending} waiting to sync` : 'Online, all synced';
  return (
    <Popover
      align="right"
      label="Sync"
      trigger={({ open, toggle, id }) => (
        <button type="button" aria-haspopup="menu" aria-expanded={open} aria-controls={id} onClick={toggle} title={label} aria-label={label} className={cx('relative inline-flex min-h-touch items-center gap-1.5 rounded-tag px-2.5 text-sm font-semibold hover:bg-sunken', tone)}>
          {online ? <Cloud aria-hidden className="h-5 w-5" /> : <CloudOff aria-hidden className="h-5 w-5" />}
          {syncing ? <RefreshCw aria-hidden className="h-3.5 w-3.5 animate-spin" /> : waiting > 0 && <span className="tabular">{waiting}</span>}
          <span className={cx('hidden xl:inline', online && !waiting && 'sr-only')}>{online ? (waiting ? 'To sync' : '') : 'Offline'}</span>
        </button>
      )}
    >
      {() => (
        <div className="w-80 p-2">
          <p className="text-sm font-semibold">{label}</p>
          <p className="mt-0.5 text-xs text-muted">Scan batches are kept on this device until the server accepts them.</p>
          {entries.length > 0 && (
            <ul className="ledger mt-2 max-h-64 overflow-auto rounded-tag border border-line text-sm">
              {entries.map((e) => (
                <li key={e.key} className="flex items-center gap-2 px-2.5 py-2">
                  <span className="min-w-0 flex-1">
                    <span className="block truncate">{e.label ?? 'Scan batch'}</span>
                    <span className={cx('block truncate text-xs', e.status === 'failed' ? 'text-danger' : 'text-muted')}>
                      {e.size !== undefined && `${count(e.size)} tags · `}
                      {e.status === 'failed' ? e.lastError : <DateText value={new Date(e.createdAt)} mode="ago" />}
                    </span>
                  </span>
                  {e.status === 'failed' && (
                    <>
                      <Button size="sm" variant="ghost" onClick={() => void retryEntry(e.key)}>
                        Retry
                      </Button>
                      <Button size="sm" variant="quiet" onClick={() => void removeEntry(e.key)}>
                        Discard
                      </Button>
                    </>
                  )}
                </li>
              ))}
            </ul>
          )}
          {pending > 0 && (
            <Button block className="mt-2" loading={syncing} disabled={!online} onClick={() => void syncNow()}>
              Sync now
            </Button>
          )}
        </div>
      )}
    </Popover>
  );
}

function AlertsBell() {
  const { queryValue } = useBranch();
  const alerts = useAlerts(queryValue);
  const list = alerts.data ?? [];
  return (
    <Popover
      align="right"
      label="Alerts"
      trigger={({ open, toggle, id }) => (
        <IconButton label={list.length ? `${list.length} open alerts` : 'Alerts'} aria-haspopup="menu" aria-expanded={open} aria-controls={id} onClick={toggle} badge={list.length}>
          <Bell aria-hidden className="h-5 w-5" />
        </IconButton>
      )}
    >
      {(close) => (
        <div className="w-80">
          <p className="px-2.5 pb-1 pt-2 text-sm font-semibold">Alerts</p>
          {list.length === 0 ? (
            <p className="px-2.5 pb-3 text-sm text-muted">{alerts.isError ? 'Alerts are not available yet.' : 'Nothing needs attention right now.'}</p>
          ) : (
            <ul className="ledger max-h-80 overflow-auto">
              {list.map((a) => (
                <li key={a.id}>
                  <Link to={a.href ?? '/'} onClick={close} className="flex gap-2.5 px-2.5 py-2.5 hover:bg-sunken">
                    <TriangleAlert aria-hidden className={cx('mt-0.5 h-4 w-4 shrink-0', a.severity === 'CRITICAL' ? 'text-danger' : 'text-warn')} />
                    <span className="min-w-0">
                      <span className="block text-sm font-medium">{a.title}</span>
                      <span className="block text-xs text-muted">
                        <DateText value={a.createdAt} mode="ago" />
                      </span>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Popover>
  );
}

function ThemeToggle() {
  const { choice, setChoice } = useTheme();
  const Icon = choice === 'light' ? Sun : choice === 'dark' ? Moon : Monitor;
  return (
    <Popover
      align="right"
      label="Theme"
      trigger={({ open, toggle, id }) => (
        <IconButton label={`Theme: ${choice === 'system' ? 'match device' : choice}`} aria-haspopup="menu" aria-expanded={open} aria-controls={id} onClick={toggle}>
          <Icon aria-hidden className="h-5 w-5" />
        </IconButton>
      )}
    >
      {(close) => (
        <>
          {(
            [
              ['light', 'Light', Sun],
              ['dark', 'Dark', Moon],
              ['system', 'Match device', Monitor],
            ] as const
          ).map(([v, l, I]) => (
            <MenuItem
              key={v}
              icon={<I />}
              onClick={() => {
                setChoice(v);
                close();
              }}
            >
              <span className="flex justify-between">
                {l}
                {choice === v && <Check aria-hidden className="h-4 w-4 text-brand" />}
              </span>
            </MenuItem>
          ))}
        </>
      )}
    </Popover>
  );
}

function UserMenu() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  if (!user) return null;
  const initials = user.name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase()).join('') || '?';
  return (
    <Popover
      align="right"
      label="Account"
      trigger={({ open, toggle, id }) => (
        <button type="button" aria-haspopup="menu" aria-expanded={open} aria-controls={id} onClick={toggle} aria-label={`Account: ${user.name}`} className="ml-1 grid h-11 w-11 place-items-center rounded-full">
          <span className="grid h-9 w-9 place-items-center rounded-full bg-brand text-sm font-bold text-brand-ink">{initials}</span>
        </button>
      )}
    >
      {(close) => (
        <div className="w-64">
          <div className="border-b border-line px-2.5 pb-2.5 pt-2">
            <p className="truncate font-semibold">{user.name}</p>
            <p className="truncate text-xs text-muted">{user.email ?? user.username}</p>
            <p className="mt-1 text-xs text-muted">{user.roleName || 'No role'}</p>
          </div>
          <div className="pt-1">
            <MenuItem
              icon={<UserRound />}
              onClick={() => {
                navigate('/account');
                close();
              }}
            >
              Account and security
            </MenuItem>
            <MenuItem
              icon={<KeyRound />}
              onClick={() => {
                navigate('/account#password');
                close();
              }}
            >
              Change password
            </MenuItem>
            <MenuItem icon={<LogOut />} onClick={() => void signOut()}>
              Sign out
            </MenuItem>
          </div>
        </div>
      )}
    </Popover>
  );
}
